import Box from '@mui/material/Box';
import { Brush } from '@visx/brush';
import { Group } from '@visx/group';
import { scaleLinear, scaleTime } from '@visx/scale';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { QUALITY_BRUSH, QUALITY_TIMELINE } from '../consts';
import { clampVisibleTimeRange } from '../lib/visible-time-range';
import { useReportViewStore } from '../store/view.store';
import { useEffectiveVisibleRange } from '../store/useSyncViewStore';
import { BrushHandle } from './BrushHandle';
import { BrushMinimap } from './BrushMinimap';
import { MinimapConfig } from './minimap-config';
import { RangeBrushLabelColumn } from './RangeBrushLabelColumn';
import { StreamQualityReportResponseDto } from '../stream-quality.types';

export type RangeBrushProps = {
  width: number;
  report: StreamQualityReportResponseDto;
  minSelectionMs?: number;
  /** Optional minimap tuning (opacity, per-bin health rule). */
  minimapConfig?: MinimapConfig;
  /** Stations included in minimap aggregation (e.g. same subset as the filtered timeline). */
  minimapStationIds?: string[];
};

/**
 * Overview time brush (visx): drag handles to zoom, drag the selection to pan. Rendered above the
 * main timeline so it sits above the time axis. Keeps the same horizontal inner width as the chart.
 * Remounts when `selectedRange` changes from outside the brush so other page widgets can drive the same window.
 */
export function RangeBrush({
  width,
  report,
  minSelectionMs,
  minimapConfig,
  minimapStationIds,
}: RangeBrushProps) {
  const fullDomain = useMemo(
    () => ({
      from: new Date(report.data.from),
      to: new Date(report.data.to),
    }),
    [report.data.from, report.data.to],
  );
  const selectedRange = useEffectiveVisibleRange(report);
  const onSelectedRangeChange = useReportViewStore((state) => state.setVisibleRangeFromDates);
  const resetVisibleRangeToDomain = useReportViewStore((state) => state.resetVisibleRangeToDomain);
  const margin = QUALITY_TIMELINE.margin;
  const innerWidth = width - margin.left - margin.right;

  const domainStartMs = fullDomain.from.getTime();
  const domainEndMs = fullDomain.to.getTime();

  const lastEmittedRef = useRef({
    from: selectedRange.from.getTime(),
    to: selectedRange.to.getTime(),
  });
  const isBrushingRef = useRef(false);
  const [brushVersion, setBrushVersion] = useState(0);

  useLayoutEffect(() => {
    const f = selectedRange.from.getTime();
    const t = selectedRange.to.getTime();
    const last = lastEmittedRef.current;
    if (f === last.from && t === last.to) {
      return;
    }
    if (isBrushingRef.current) {
      return;
    }
    lastEmittedRef.current = { from: f, to: t };
    setBrushVersion((v) => v + 1);
  }, [selectedRange.from, selectedRange.to]);

  const xBrushScale = useMemo(() => {
    return scaleTime<number>({
      domain: [fullDomain.from, fullDomain.to],
      range: [0, innerWidth],
    });
  }, [fullDomain.from, fullDomain.to, innerWidth]);

  const yBrushScale = useMemo(() => {
    return scaleLinear<number>({
      domain: [0, QUALITY_BRUSH.innerHeight],
      range: [0, QUALITY_BRUSH.innerHeight],
    });
  }, []);

  const initialBrushPosition = useMemo(() => {
    const x0 = xBrushScale(selectedRange.from);
    const x1 = xBrushScale(selectedRange.to);
    if (x0 === undefined || x1 === undefined || Number.isNaN(x0) || Number.isNaN(x1)) {
      return {
        start: { x: 0, y: 0 },
        end: { x: innerWidth, y: QUALITY_BRUSH.innerHeight },
      };
    }
    return {
      start: { x: Math.min(x0, x1), y: 0 },
      end: { x: Math.max(x0, x1), y: QUALITY_BRUSH.innerHeight },
    };
  }, [selectedRange.from, selectedRange.to, xBrushScale, innerWidth, brushVersion]);

  const applyBounds = (bounds: { x0: number; x1: number } | null) => {
    if (!bounds) {
      return;
    }
    const clamped = clampVisibleTimeRange(
      Number(bounds.x0),
      Number(bounds.x1),
      domainStartMs,
      domainEndMs,
      minSelectionMs,
    );
    lastEmittedRef.current = {
      from: clamped.from.getTime(),
      to: clamped.to.getTime(),
    };
    onSelectedRangeChange(clamped);
  };

  const handleBrushChange = (bounds: { x0: number; x1: number } | null) => {
    applyBounds(bounds);
  };

  const handleBrushEnd = (bounds: { x0: number; x1: number } | null) => {
    isBrushingRef.current = false;
    applyBounds(bounds);
  };

  const isFullReportRange = useMemo(() => {
    return (
      selectedRange.from.getTime() === fullDomain.from.getTime() &&
      selectedRange.to.getTime() === fullDomain.to.getTime()
    );
  }, [selectedRange.from, selectedRange.to, fullDomain.from, fullDomain.to]);

  const handleResetToFullRange = useCallback(() => {
    resetVisibleRangeToDomain();
    lastEmittedRef.current = {
      from: fullDomain.from.getTime(),
      to: fullDomain.to.getTime(),
    };
  }, [fullDomain.from, fullDomain.to, resetVisibleRangeToDomain]);

  const brushSvgWidth = width - margin.left;
  /**
   * Visx places handles at extent ± handleSize/2; the custom grip path extends slightly past that
   * (path min x -4.5 vs handle box -4). Inset content so the left handle is not clipped at x0 === 0.
   */
  const brushEdgePadPx = QUALITY_BRUSH.handleSize / 2 + 1;
  const brushSvgOuterWidth = brushSvgWidth + brushEdgePadPx;

  if (width < 10 || innerWidth < 10 || brushSvgWidth < 10 || !(domainEndMs > domainStartMs)) {
    return null;
  }

  return (
    <Box
      sx={{
        width: '100%',
        overflowX: 'auto',
        overflowY: 'visible',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
      }}
    >
      <RangeBrushLabelColumn
        widthPx={margin.left}
        canReset={!isFullReportRange}
        onReset={handleResetToFullRange}
      />
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          width: brushSvgWidth,
          maxWidth: brushSvgWidth,
          marginLeft: `${-brushEdgePadPx}px`,
          overflow: 'visible',
        }}
      >
        <svg
          width={brushSvgOuterWidth}
          height={QUALITY_BRUSH.innerHeight}
          style={{ display: 'block', overflow: 'visible' }}
        >
          <Group left={brushEdgePadPx} top={0}>
            <BrushMinimap
              report={report}
              fullDomain={fullDomain}
              innerWidth={innerWidth}
              height={QUALITY_BRUSH.innerHeight}
              minimapConfig={minimapConfig}
              stationIds={minimapStationIds}
            />
            {/* Brush spans this SVG only; `margin.left` lives in the sibling label column. */}
            <Brush
              key={brushVersion}
              xScale={xBrushScale}
              yScale={yBrushScale}
              width={innerWidth}
              height={QUALITY_BRUSH.innerHeight}
              margin={{ top: 0, left: 0, right: margin.right, bottom: 0 }}
              brushDirection="horizontal"
              handleSize={QUALITY_BRUSH.handleSize}
              resizeTriggerAreas={['left', 'right']}
              useWindowMoveEvents
              renderBrushHandle={(props) => <BrushHandle {...props} />}
              onBrushStart={() => {
                isBrushingRef.current = true;
              }}
              onChange={handleBrushChange}
              onBrushEnd={handleBrushEnd}
              initialBrushPosition={initialBrushPosition}
              disableDraggingSelection={false}
              selectedBoxStyle={{
                fill: QUALITY_BRUSH.selectionColor,
                fillOpacity: 0,
                stroke: QUALITY_BRUSH.selectionColor,
                strokeWidth: 1,
                strokeOpacity: 0.9,
              }}
            />
          </Group>
        </svg>
      </Box>
    </Box>
  );
}
